
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { dbService } from '../lib/firebase';
import { CalendarEvent, RotationWeek, User } from '../types';
import { USERS } from '../constants';
import { Phone, MapPin, ChevronLeft, ChevronRight, X, Save, Building2, Plane, Calendar, ArrowRight, Trash2, Edit2, Printer, Plus } from 'lucide-react';

interface CalendarSystemProps {
  events: CalendarEvent[];
  rotations: RotationWeek[];
  onSaveEvent: (event: CalendarEvent) => void;
  onDeleteEvent: (date: string, category: string) => void;
  onSaveRotation: (rotation: RotationWeek) => void;
}

export const CalendarSystem: React.FC<CalendarSystemProps> = ({ events, rotations, onSaveEvent, onDeleteEvent, onSaveRotation }) => {
  const [activeTab, setActiveTab] = useState<'on-call' | 'assignments'>('on-call');
  const [users, setUsers] = useState<User[]>([]);
  
  // --- On-Call State ---
  const [currentDate, setCurrentDate] = useState(new Date()); 
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ attending: '', resident: '', anesthesia: '' });

  // --- Assignments State ---
  const [currentRotationIndex, setCurrentRotationIndex] = useState(0);
  const [isEditRotationModalOpen, setIsEditRotationModalOpen] = useState(false);
  const [rotationEditForm, setRotationEditForm] = useState<{residentId: string, assignment: string}[]>([]);
  
  const hasInitializedRotations = useRef(false);

  useEffect(() => {
    const loadUsers = async () => {
        const fetchedUsers = await dbService.getUsers();
        setUsers(fetchedUsers);
    };
    loadUsers();
  }, []);

  // Calculate closest rotation week on mount
  useEffect(() => {
      if(rotations.length === 0 || hasInitializedRotations.current) return;
      
      const today = new Date().toISOString().split('T')[0];
      const index = rotations.findIndex(w => w.weekStart >= today);
      
      if (index !== -1) {
          // If the found index is in the future, show the one before it (current week) if possible
          if(index > 0 && rotations[index].weekStart > today) {
              setCurrentRotationIndex(index - 1);
          } else {
              setCurrentRotationIndex(index);
          }
      } else {
          // If no future date found, show last one
          setCurrentRotationIndex(rotations.length - 1);
      }
      hasInitializedRotations.current = true;
  }, [rotations]); 

  // --- On-Call Logic ---
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay, month, year };
  };

  const { days, firstDay, month, year } = getDaysInMonth(currentDate);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const handleDayClick = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const event = events.find(e => e.date === dateStr && e.category === 'on-call');
    
    setSelectedDay(dateStr);
    setEditForm({
      attending: event?.onCallTeam?.attending || '',
      resident: event?.onCallTeam?.resident || '',
      anesthesia: event?.onCallTeam?.anesthesia || ''
    });
  };

  const handleSave = () => {
    if (!selectedDay) return;
    
    if (editForm.attending || editForm.resident || editForm.anesthesia) {
        const newEvent: CalendarEvent = {
            id: `evt-${selectedDay}-on-call`, 
            title: 'Surgery Team',
            date: selectedDay,
            category: 'on-call',
            onCallTeam: {
                attending: editForm.attending,
                resident: editForm.resident,
                anesthesia: editForm.anesthesia
            }
        };
        onSaveEvent(newEvent);
    } else {
        onDeleteEvent(selectedDay, 'on-call');
    }
    
    setSelectedDay(null);
  };

  const handleDelete = () => {
      if (!selectedDay) return;
      onDeleteEvent(selectedDay, 'on-call');
      setSelectedDay(null);
  };

  const handlePrintOnCall = () => {
    const printWindow = window.open('', '_blank', 'width=1100,height=800,resizable=yes,scrollbars=yes');
    if (!printWindow) {
        alert("Please allow popups to print the schedule.");
        return;
    }

    const monthName = monthNames[month];
    const title = `${monthName} ${year} On-Call Schedule`;
    
    let calendarHtml = '<div class="calendar-grid">';
    
    ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'].forEach(day => {
        calendarHtml += `<div class="header-cell">${day}</div>`;
    });

    for (let i = 0; i < firstDay; i++) {
        calendarHtml += `<div class="day-cell empty"></div>`;
    }

    for (let day = 1; day <= days; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const event = events.find(e => e.date === dateStr && e.category === 'on-call');
        
        let content = '';
        if (event?.onCallTeam) {
            // Clean names-only print as requested
            if (event.onCallTeam.attending) content += `<div class="badge attending">${event.onCallTeam.attending}</div>`;
            if (event.onCallTeam.resident) content += `<div class="badge resident">${event.onCallTeam.resident}</div>`;
            if (event.onCallTeam.anesthesia) {
                const isOpen = event.onCallTeam.anesthesia.trim().toUpperCase() === 'OPEN';
                content += `<div class="badge anesthesia ${isOpen ? 'open' : ''}">${event.onCallTeam.anesthesia}</div>`;
            }
        }

        calendarHtml += `
            <div class="day-cell">
                <div class="day-number">${day}</div>
                <div class="events">${content}</div>
            </div>
        `;
    }
    
    calendarHtml += '</div>';

    printWindow.document.open();
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${title}</title>
            <style>
                body { font-family: -apple-system, system-ui, sans-serif; padding: 20px; color: #1e293b; }
                h1 { text-align: center; margin-bottom: 20px; color: #1e293b; font-size: 24px; text-transform: uppercase; letter-spacing: 2px; }
                .calendar-grid { 
                    display: grid; 
                    grid-template-columns: repeat(7, 1fr); 
                    border: 1px solid #cbd5e1;
                    width: 100%;
                }
                .header-cell {
                    background: #1e3a8a;
                    color: white;
                    padding: 10px;
                    text-align: center;
                    font-size: 12px;
                    font-weight: bold;
                    border: 1px solid #cbd5e1;
                    -webkit-print-color-adjust: exact; 
                    print-color-adjust: exact;
                }
                .day-cell {
                    border: 1px solid #cbd5e1;
                    min-height: 120px;
                    padding: 8px;
                    position: relative;
                    page-break-inside: avoid;
                    background: white;
                }
                .day-cell.empty { background: #f1f5f9; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .day-number { 
                    font-weight: bold; 
                    color: #64748b; 
                    margin-bottom: 8px;
                    text-align: right;
                    font-size: 14px;
                }
                .events { display: flex; flex-direction: column; gap: 4px; }
                .badge {
                    padding: 3px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: bold;
                    text-transform: uppercase;
                    -webkit-print-color-adjust: exact; 
                    print-color-adjust: exact;
                }
                .attending { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
                .resident { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
                .anesthesia { background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
                .anesthesia.open { background: #fef9c3; color: #15803d; border: 1px solid #fde047; font-weight: bold; }
                
                @media print {
                    @page { size: landscape; margin: 0.5cm; }
                    body { padding: 0; }
                }
            </style>
        </head>
        <body>
            <h1>${title}</h1>
            ${calendarHtml}
        </body>
        </html>
    `);
    printWindow.document.close();

    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
    }, 500);
  };

  const renderMonthGrid = () => {
    const blanks = Array(firstDay).fill(null);
    const dayNumbers = Array.from({ length: days }, (_, i) => i + 1);
    
    return (
      <div className="grid grid-cols-7 border-l border-t border-slate-300 dark:border-slate-600 bg-slate-200 dark:bg-slate-700 gap-px shadow-sm">
        {['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'].map(day => (
          <div key={day} className="bg-blue-900 text-white p-2 text-center text-xs font-bold uppercase tracking-wider">
            {day}
          </div>
        ))}
        
        {[...blanks, ...dayNumbers].map((day, index) => {
            if (day === null) return <div key={`blank-${index}`} className="bg-slate-50 dark:bg-slate-900/50 min-h-[100px]"></div>;

            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const event = events.find(e => e.date === dateStr && e.category === 'on-call');

            return (
                <div 
                    key={day} 
                    onClick={() => handleDayClick(day)}
                    className="bg-white dark:bg-slate-800 min-h-[100px] p-2 relative hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer transition-colors flex flex-col items-center justify-start text-center border-b border-r border-slate-300 dark:border-slate-600 group"
                >
                    <span className="absolute top-1 right-2 text-slate-400 dark:text-slate-500 font-bold text-sm group-hover:text-blue-900 dark:group-hover:text-blue-400">{day}</span>
                    
                    {event?.onCallTeam ? (
                        <div className="mt-5 flex flex-col gap-0.5 w-full">
                            {event.onCallTeam.attending && (
                                <div className="text-red-700 dark:text-red-400 font-bold text-xs md:text-sm uppercase truncate px-1 bg-red-50 dark:bg-red-900/30 rounded">
                                    {event.onCallTeam.attending}
                                </div>
                            )}
                             {event.onCallTeam.resident && (
                                <div className="text-blue-700 dark:text-blue-400 font-bold text-xs md:text-sm uppercase truncate px-1 bg-blue-50 dark:bg-blue-900/30 rounded">
                                    {event.onCallTeam.resident}
                                </div>
                            )}
                             {event.onCallTeam.anesthesia && (
                                <div className={`font-medium text-xs md:text-sm truncate px-1 rounded ${
                                    event.onCallTeam.anesthesia.trim().toUpperCase() === 'OPEN'
                                        ? 'text-emerald-800 dark:text-emerald-300 bg-amber-100 dark:bg-amber-400/20 font-bold border border-amber-300 dark:border-amber-500/30'
                                        : 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30'
                                }`}>
                                    {event.onCallTeam.anesthesia}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-full w-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                             <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1 rounded shadow-sm">Edit</span>
                        </div>
                    )}
                </div>
            );
        })}
      </div>
    );
  };

  // --- Assignments Logic ---
  const currentRotation = rotations[currentRotationIndex];
  
  const handlePrevWeek = () => { 
      if (currentRotationIndex > 0) {
          setCurrentRotationIndex(prev => prev - 1); 
      } else {
          if (!currentRotation) return;
          const [y, m, d] = currentRotation.weekStart.split('-').map(Number);
          const dateObj = new Date(y, m - 1, d);
          dateObj.setDate(dateObj.getDate() - 7);
          
          const newY = dateObj.getFullYear();
          const newM = String(dateObj.getMonth() + 1).padStart(2, '0');
          const newD = String(dateObj.getDate()).padStart(2, '0');
          const safeNewStart = `${newY}-${newM}-${newD}`;

          const newWeek: RotationWeek = {
              weekStart: safeNewStart,
              assignments: users.filter(u => u.role === 'Resident').map(r => ({ residentId: r.id, assignment: '' }))
          };
          
          onSaveRotation(newWeek);
      }
  };

  const handleNextWeek = () => { if (currentRotationIndex < rotations.length - 1) setCurrentRotationIndex(prev => prev + 1); };
  
  const handleOpenEditRotation = () => {
      if(!currentRotation) return;
      const residentsRaw = users.filter(u => u.role === 'Resident');
      // Deduplicate residents by last name (prefer the one with an email/auth over the mock one)
      const residents = Object.values(residentsRaw.reduce((acc, curr) => {
          if (!acc[curr.lastName]) {
              acc[curr.lastName] = curr;
          } else if (curr.email && !acc[curr.lastName].email) {
              acc[curr.lastName] = curr;
          }
          return acc;
      }, {} as Record<string, User>));
      const form = residents.map(r => {
          const mockUser = USERS.find(u => u.lastName === r.lastName);
          const originalId = mockUser ? mockUser.id : r.id;
          const existing = currentRotation.assignments.find(a => a.residentId === r.id || a.residentId === originalId);
          return { residentId: r.id, assignment: existing ? existing.assignment : '' };
      });
      setRotationEditForm(form);
      setIsEditRotationModalOpen(true);
  };

  const handleSaveRotationEdit = () => {
      if(!currentRotation) return;
      const updatedRotation: RotationWeek = {
          ...currentRotation,
          assignments: rotationEditForm.filter(f => f.assignment.trim() !== '')
      };
      onSaveRotation(updatedRotation);
      setIsEditRotationModalOpen(false);
  };

  const organizedAssignments = useMemo(() => {
    const mission: { name: string; assignment: string }[] = [];
    const op: { name: string; assignment: string }[] = [];
    const off: { name: string; assignment: string }[] = [];

    if (!currentRotation) return { mission, op, off };

    const residentsRaw = users.filter(u => u.role === 'Resident');
      // Deduplicate residents by last name (prefer the one with an email/auth over the mock one)
      const residents = Object.values(residentsRaw.reduce((acc, curr) => {
          if (!acc[curr.lastName]) {
              acc[curr.lastName] = curr;
          } else if (curr.email && !acc[curr.lastName].email) {
              acc[curr.lastName] = curr;
          }
          return acc;
      }, {} as Record<string, User>));
    residents.forEach(resident => {
      const mockUser = USERS.find(u => u.lastName === resident.lastName);
      const originalId = mockUser ? mockUser.id : resident.id;
      const item = currentRotation.assignments.find(a => a.residentId === resident.id || a.residentId === originalId);
      const assignment = item ? item.assignment : '';
      const assignmentLower = assignment.toLowerCase();
      const isMission = assignmentLower.includes('friesen') || assignmentLower.includes('wilson') || assignmentLower.includes('marquiss') || assignmentLower.includes('weinstein');
      const isOp = assignmentLower.includes('allen') || assignmentLower.includes('aeschlimann');
      const record = { name: `Dr. ${resident.lastName}`, assignment: assignment };
      if (assignment && isMission) { mission.push(record); } else if (assignment && isOp) { op.push(record); } else { off.push(record); }
    });
    return { mission, op, off };
  }, [currentRotation, users]);

  const renderServiceAssignments = () => {
    if (!currentRotation) return <div className="p-12 text-center text-slate-400">No schedule data available.</div>;
    const [y, m, d] = currentRotation.weekStart.split('-');
    const formattedDate = new Date(parseInt(y), parseInt(m)-1, parseInt(d)).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    return (
        <div className="max-w-7xl mx-auto py-2 space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
                <button onClick={handlePrevWeek} className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-900 dark:hover:text-blue-400 font-medium transition-colors group">
                  <div className="p-1 rounded-full bg-slate-100 dark:bg-slate-900 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50">
                    {currentRotationIndex === 0 ? <Plus className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> : <ChevronLeft className="w-4 h-4" />}
                  </div>
                  {currentRotationIndex === 0 ? 'Add Previous Week' : 'Previous Week'}
                </button>
                <div className="flex flex-col items-center">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Week Of</span>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-800 dark:text-white font-serif"><Calendar className="w-5 h-5 text-blue-900 dark:text-blue-400" />{formattedDate}</div>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={handleOpenEditRotation} className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 rounded-lg text-sm font-bold border border-blue-200 dark:border-blue-800 hover:bg-blue-100">
                        <Edit2 className="w-4 h-4" /> Edit
                    </button>
                    <button onClick={handleNextWeek} disabled={currentRotationIndex === rotations.length - 1} className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-900 disabled:opacity-30 font-medium transition-colors">
                    Next Week <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>
            
            <div className="md:hidden w-full mb-4">
                 <button onClick={handleOpenEditRotation} className="w-full flex justify-center items-center gap-2 px-3 py-3 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300 rounded-lg text-sm font-bold border border-blue-200 dark:border-blue-800">
                    <Edit2 className="w-4 h-4" /> Edit Assignments
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Mission */}
                <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden min-h-[320px]">
                    <div className="bg-blue-900 dark:bg-blue-950 text-white p-6">
                        <div className="flex items-center gap-3"><Building2 className="w-8 h-8 opacity-80" /><div><h3 className="text-2xl font-bold brand-font">Mission Med Vet</h3><p className="text-blue-200 text-sm font-medium">Service Chiefs: Friesen • Wilson • Marquiss</p></div></div>
                    </div>
                    <div className="flex-1 p-6 bg-slate-50 dark:bg-slate-900/50">
                        {organizedAssignments.mission.length > 0 ? (
                           <ul className="space-y-3">
                             {organizedAssignments.mission.map((rec, idx) => (
                               <li key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30 shadow-sm flex justify-between items-center">
                                   <span className="font-bold text-slate-800 dark:text-slate-200 text-lg font-serif">{rec.name}</span>
                                   <span className="text-xs font-bold uppercase text-blue-900 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/50 px-3 py-1 rounded-full">{rec.assignment}</span>
                               </li>
                             ))}
                           </ul>
                        ) : (<div className="h-full flex items-center justify-center text-slate-400 italic">No residents assigned this week.</div>)}
                    </div>
                </div>

                {/* OP */}
                <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden min-h-[320px]">
                    <div className="bg-blue-800 dark:bg-blue-900 text-white p-6">
                        <div className="flex items-center gap-3"><Building2 className="w-8 h-8 opacity-80" /><div><h3 className="text-2xl font-bold brand-font">OP Med Vet</h3><p className="text-blue-200 text-sm font-medium">Service Chiefs: Allen • Aeschlimann</p></div></div>
                    </div>
                    <div className="flex-1 p-6 bg-slate-50 dark:bg-slate-900/50">
                        {organizedAssignments.op.length > 0 ? (
                           <ul className="space-y-3">
                             {organizedAssignments.op.map((rec, idx) => (
                               <li key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30 shadow-sm flex justify-between items-center">
                                   <span className="font-bold text-slate-800 dark:text-slate-200 text-lg font-serif">{rec.name}</span>
                                   <span className="text-xs font-bold uppercase text-blue-800 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/50 px-3 py-1 rounded-full">{rec.assignment}</span>
                               </li>
                             ))}
                           </ul>
                        ) : (<div className="h-full flex items-center justify-center text-slate-400 italic">No residents assigned this week.</div>)}
                    </div>
                </div>
            </div>

            {/* Off Service */}
            <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="bg-slate-700 dark:bg-slate-800 text-white p-6">
                    <div className="flex items-center gap-3"><Plane className="w-8 h-8 opacity-80" /><div><h3 className="text-2xl font-bold brand-font">Off Service / Rotations</h3><p className="text-slate-300 text-sm font-medium">Vacation, Research, and External Rotations</p></div></div>
                </div>
                <div className="p-6 bg-slate-50 dark:bg-slate-900/50">
                   {organizedAssignments.off.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {organizedAssignments.off.map((rec, idx) => (
                          <div key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col gap-1">
                              <span className="font-bold text-slate-800 dark:text-slate-200 text-lg font-serif">{rec.name}</span>
                              <div className="flex items-center gap-2 text-slate-500 text-sm"><ArrowRight className="w-4 h-4 text-slate-300" /><span className="font-bold uppercase tracking-wide text-xs">{rec.assignment || 'Off'}</span></div>
                          </div>
                        ))}
                      </div>
                   ) : (<div className="py-8 text-center text-slate-400 italic">No residents off service this week.</div>)}
                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden relative">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white brand-font">
                    {activeTab === 'on-call' ? 'On-Call Surgery Team' : 'Weekly Service Assignments'}
                </h2>
                {activeTab === 'on-call' && (
                    <div className="flex items-center gap-4 mt-2">
                        <button onClick={handlePrevMonth} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded"><ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
                        <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest font-sans">{monthNames[month]} {year}</h3>
                        <button onClick={handleNextMonth} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded"><ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
                        
                        <button onClick={handlePrintOnCall} className="ml-4 p-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300 hover:text-blue-900 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors shadow-sm" title="Print Schedule">
                            <Printer className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>
            
            <div className="flex bg-slate-200 dark:bg-slate-800 p-1 rounded-lg shadow-inner">
                <button onClick={() => setActiveTab('on-call')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'on-call' ? 'bg-white dark:bg-slate-700 text-blue-900 dark:text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}><Phone className="w-4 h-4" /> Monthly On-Call</button>
                <button onClick={() => setActiveTab('assignments')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'assignments' ? 'bg-white dark:bg-slate-700 text-blue-900 dark:text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}><MapPin className="w-4 h-4" /> Weekly Schedule</button>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto">
            {activeTab === 'on-call' && renderMonthGrid()}
            {activeTab === 'assignments' && renderServiceAssignments()}
        </div>

        {selectedDay && (
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-slate-200 dark:border-slate-700">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                         <h3 className="font-bold text-slate-800 dark:text-white font-serif">Edit Schedule: {selectedDay}</h3>
                         <button onClick={() => setSelectedDay(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-red-600 dark:text-red-400 uppercase mb-1">Attending (Red)</label>
                            <input 
                                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none uppercase font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                placeholder="e.g. FRIESEN"
                                value={editForm.attending}
                                onChange={e => setEditForm({...editForm, attending: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-blue-600 dark:text-blue-400 uppercase mb-1">Resident (Blue)</label>
                            <input 
                                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none uppercase font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                placeholder="e.g. GRISHAM"
                                value={editForm.resident}
                                onChange={e => setEditForm({...editForm, resident: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-teal-600 dark:text-teal-400 uppercase mb-1">Anesthesia (Green)</label>
                            <input 
                                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                placeholder="e.g. Jess"
                                value={editForm.anesthesia}
                                onChange={e => setEditForm({...editForm, anesthesia: e.target.value})}
                            />
                        </div>
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-between border-t border-slate-200 dark:border-slate-700">
                         <button onClick={handleDelete} className="px-4 py-2 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-2">
                            <Trash2 className="w-4 h-4" /> Clear Day
                        </button>
                        <div className="flex gap-2">
                            <button onClick={() => setSelectedDay(null)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                            <button onClick={handleSave} className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 flex items-center gap-2">
                                <Save className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {isEditRotationModalOpen && currentRotation && (
             <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh]">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                         <h3 className="font-bold text-slate-800 dark:text-white font-serif">Edit Rotation: {currentRotation.weekStart}</h3>
                         <button onClick={() => setIsEditRotationModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-6 space-y-4 overflow-y-auto">
                        {rotationEditForm.map((entry, idx) => {
                            const resident = users.find(u => u.id === entry.residentId);
                            if (!resident) return null;
                            return (
                                <div key={entry.residentId} className="border-b border-slate-100 dark:border-slate-700 pb-3 last:border-0">
                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Dr. {resident?.lastName}</label>
                                    <input 
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        placeholder="Enter assignment (e.g. Friesen, Research...)"
                                        value={entry.assignment}
                                        onChange={e => {
                                            const newVal = e.target.value;
                                            setRotationEditForm(prev => prev.map((item, i) => i === idx ? { ...item, assignment: newVal } : item));
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                        <button onClick={() => setIsEditRotationModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                        <button onClick={handleSaveRotationEdit} className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 flex items-center gap-2">
                            <Save className="w-4 h-4" /> Save Changes
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};
